// Rider-Auth Client
// -----------------
//
// Wave 14bs: Kundendaten-Login. HttpOnly-Cookie + localStorage-Backup
// (iOS-PWA-Cookie-Eviction-Workaround analog stagingAuth).
//
// Token-Persistence-Strategie:
//   - Bei erfolgreichem /verify: Token kommt als JSON mit → in localStorage
//     (Key `adt_user_token`). Cookie setzt der Server parallel als HttpOnly,
//     damit normale Browser-Fetches das Cookie automatisch mitschicken.
//   - Für fetch mit Authorization-Header (fallback falls Cookie evicted):
//     `authFetch(url, opts)` legt das Token als Bearer mit.
//   - Session-Sanity-Check beim App-Start: GET /api/me. Wenn 401 → Token
//     löschen, User muss neu einloggen.

const TOKEN_KEY = 'adt_user_token';
// Letzter bekannter User. Anker fuer den Kaltstart, wenn /api/me nicht
// erreichbar ist (siehe fetchSession) — ohne den landet ein Offline-Start
// auf dem Login-Screen, obwohl die Session laengst gueltig ist.
const USER_KEY = 'adt_user_cached';

export type RiderRole = 'user' | 'admin_light' | 'admin_light_plus' | 'admin';

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: RiderRole;
  createdAt: string;
}

// Wave 14cb: 4-stufige Hierarchie. Kleines Helper damit UI-Code nicht
// mehrfach role-Strings vergleichen muss. Entspricht backend/lib/requireAdmin.js.
const RIDER_ROLE_LEVEL: Record<RiderRole, number> = {
  user: 0,
  admin_light: 1,
  admin_light_plus: 2,
  admin: 3,
};
export function roleAtLeast(actual: RiderRole | undefined | null, required: RiderRole): boolean {
  if (!actual) return false;
  return (RIDER_ROLE_LEVEL[actual] ?? -1) >= (RIDER_ROLE_LEVEL[required] ?? -1);
}

export interface SessionState {
  user: SessionUser | null;
  loading: boolean;
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private mode — nur Cookie als Anker */
  }
}

export function clearStoredToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
}

export function getCachedUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

export function setCachedUser(user: SessionUser) {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* private mode — dann eben kein Offline-Kaltstart */
  }
}

export function clearCachedUser() {
  try {
    localStorage.removeItem(USER_KEY);
  } catch {
    /* noop */
  }
}

/**
 * fetch-Wrapper, der automatisch das Bearer-Token mitschickt falls vorhanden.
 * Cookies werden vom Browser ohnehin automatisch beigefügt; der Header ist
 * der localStorage-Fallback für iOS-PWA-nach-Kill-Eviction.
 */
export async function authFetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const token = getStoredToken();
  const headers = new Headers(init.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers, credentials: 'include' });
}

/**
 * Ergebnis des Session-Checks. Der Unterschied zwischen „abgemeldet" und
 * „Server nicht erreichbar" ist der Kern des Bugs vom 2026-08-26:
 *
 *   Beim Kaltstart der PWA feuert AuthGate sofort GET /api/me. Ist das Netz
 *   in dem Moment noch nicht da (auf iOS beim App-Start die Regel, nicht die
 *   Ausnahme), greift im Service Worker der Offline-Short-Circuit und das
 *   Safety-Net liefert eine synthetische 504. Die alte fetchMe() hat daraus
 *   `null` gemacht — nicht unterscheidbar von einem echten 401 — und der
 *   AuthGate hat den User auf den Login-Screen geworfen, obwohl seine
 *   Session 30 Tage gueltig ist. Symptom: „ich muss mich bei jedem
 *   App-Start neu einloggen."
 *
 * Nur ein echtes 401/403 meldet ab. Alles andere heisst „weiss ich grad
 * nicht" — dann gilt der zuletzt bekannte Zustand weiter.
 */
export type SessionCheck =
  | { status: 'signed-in'; user: SessionUser }
  | { status: 'signed-out' }
  | { status: 'unreachable' };

export async function fetchSession(): Promise<SessionCheck> {
  try {
    const res = await authFetch('/api/me', { method: 'GET' });
    if (res.status === 401 || res.status === 403) {
      // Der Server sagt ausdruecklich nein — erst hier wird abgemeldet.
      clearStoredToken();
      clearCachedUser();
      return { status: 'signed-out' };
    }
    if (!res.ok) {
      // 504 vom Offline-Safety-Net, 502/503 beim Deploy, Gateway-Timeout …
      console.info('[auth] /api/me nicht erreichbar:', res.status);
      return { status: 'unreachable' };
    }
    const body = await res.json();
    const user = body.user as SessionUser;
    setCachedUser(user);
    return { status: 'signed-in', user };
  } catch (err) {
    console.info('[auth] /api/me fehlgeschlagen', err);
    return { status: 'unreachable' };
  }
}

/**
 * Bequemer Wrapper fuer Aufrufer, die nur „welche Rolle hat der User"
 * wissen wollen. ACHTUNG: `null` heisst hier weiterhin „kein User bekannt",
 * egal ob abgemeldet oder offline — fuer Auth-Entscheidungen ist
 * fetchSession() die richtige Funktion.
 */
export async function fetchMe(): Promise<SessionUser | null> {
  const res = await fetchSession();
  if (res.status === 'signed-in') return res.user;
  if (res.status === 'unreachable') return getCachedUser();
  return null;
}

export interface SignupInput {
  email: string;
  firstName: string;
  lastName: string;
  inviteCode: string;
  acceptedPrivacy: boolean;
}

/**
 * Wave 14bz: prüft einen EAC/Invite-Code, ohne einen Account zu erzeugen.
 * Wird vom Landing-Screen aufgerufen: erst wenn valid → zeigen wir die
 * Signup-Felder (Name+Email+Privacy). So ist der EAC das klare Gate.
 *
 * Wave 14cb: Backdoor-Shortcut. Wenn der Server `backdoor: true` zurückgibt
 * (= ALLOW_BACKDOOR=true UND der eingegebene Code matcht ADT_BACKDOOR_PASSWORD,
 * env-gated, in production hard-disabled), ist der User SOFORT eingeloggt
 * — kein Signup-Flow, kein Magic-Link. Wir spiegeln den Token in
 * localStorage und reichen den User durch; AuthGate handelt das `backdoor`-
 * Flag und springt direkt in den eingeloggten State.
 */
export async function checkEarlyAccessCode(
  code: string,
): Promise<{
  valid: boolean;
  /** Wave 14cb: Server hat den Backdoor-Code akzeptiert; Login bereits durch. */
  backdoor?: boolean;
  /** Code existiert, ist aber schon eingeloest → eigene Fehlermeldung. */
  used?: boolean;
  user?: SessionUser;
}> {
  try {
    const res = await fetch('/api/auth/check-eac', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim() }),
      credentials: 'include',
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok && body?.backdoor && body?.token && body?.user) {
      // Token in localStorage spiegeln (gleicher Pattern wie verifyToken /
      // submitLogin → siehe oben). Cookie hat der Server schon gesetzt.
      setStoredToken(body.token);
      return { valid: true, backdoor: true, user: body.user as SessionUser };
    }
    return { valid: res.ok && !!body?.valid, used: !!body?.used };
  } catch {
    return { valid: false };
  }
}

export async function submitSignup(input: SignupInput): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body?.error || `http_${res.status}` };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: 'network' };
  }
}

export interface LoginResult {
  ok: boolean;
  error?: string;
  /**
   * Wave 14bu-fix2: Server-seitige Error-Message für den Quick-Access-Pfad.
   * Nur gesetzt, wenn der QA-Login nach Password-Match wegen Session-Setup
   * fehlgeschlagen ist. Wird im UI direkt angezeigt, damit wir keinen Server-
   * Log-Zugriff brauchen um zu diagnostizieren was schief läuft.
   */
  detail?: string;
  /** Wave 14bu: Quick-Access-Passwort gematched → direkt eingeloggt, kein Magic-Link nötig. */
  quickAccess?: boolean;
  user?: SessionUser;
  /**
   * Wave 14bx: Server hat die Email nicht in der DB. Frontend soll zum
   * Signup-Formular wechseln und die Email vorausfüllen. Trade-off: User-
   * Enumeration ist möglich, aber für Early-Access (gated by EAC) OK.
   */
  needsSignup?: boolean;
}

export async function submitLogin(email: string): Promise<LoginResult> {
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: body?.error || `http_${res.status}`,
        detail: typeof body?.detail === 'string' ? body.detail : undefined,
      };
    }
    if (body?.quickAccess && body?.token && body?.user) {
      setStoredToken(body.token);
      return { ok: true, quickAccess: true, user: body.user };
    }
    if (body?.needsSignup) {
      return { ok: true, needsSignup: true };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export interface DeviceInfo {
  id: string;
  device_name: string;
  last_seen_at: string;
  created_at: string;
}

export interface VerifyResult {
  ok: boolean;
  user?: SessionUser;
  error?: string;
  /**
   * Wave 14bz: bei device_cap_reached (HTTP 403) liefert der Server die
   * Liste der aktuell aktiven Devices, damit die UI dem User sagen kann
   * auf welchen er sich ausloggen soll.
   */
  devices?: DeviceInfo[];
  cap?: number;
}

export async function verifyToken(
  token: string,
  opts: { replaceAll?: boolean } = {},
): Promise<VerifyResult> {
  try {
    // Wave 14bz-fix: replace=all-Escape-Hatch, falls User im
    // device_cap_reached-Screen festhängt.
    const url = `/api/auth/verify?token=${encodeURIComponent(token)}${
      opts.replaceAll ? '&replace=all' : ''
    }`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 403 && body?.error === 'device_cap_reached') {
        return {
          ok: false,
          error: 'device_cap_reached',
          devices: Array.isArray(body.devices) ? body.devices : [],
          cap: typeof body.cap === 'number' ? body.cap : 2,
        };
      }
      return { ok: false, error: body?.error || `http_${res.status}` };
    }
    if (body?.token) setStoredToken(body.token);
    return { ok: true, user: body.user };
  } catch {
    return { ok: false, error: 'network' };
  }
}

/**
 * Wave 14ca-fix33: PWA-freundlicher Code-Login. Statt Magic-Link zu klicken
 * (öffnet auf iOS immer in Safari, nicht in der PWA), tippt der User den
 * 6-stelligen Code aus der Email direkt in der App ein.
 */
export async function verifyCode(
  email: string,
  code: string,
  opts: { replaceAll?: boolean } = {},
): Promise<VerifyResult> {
  try {
    const res = await fetch('/api/auth/verify-code', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        code,
        replaceAll: opts.replaceAll === true,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 403 && body?.error === 'device_cap_reached') {
        return {
          ok: false,
          error: 'device_cap_reached',
          devices: Array.isArray(body.devices) ? body.devices : [],
          cap: typeof body.cap === 'number' ? body.cap : 2,
        };
      }
      return { ok: false, error: body?.error || `http_${res.status}` };
    }
    if (body?.token) setStoredToken(body.token);
    return { ok: true, user: body.user };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export async function logout(): Promise<void> {
  try {
    await authFetch('/api/auth/logout', { method: 'POST' });
  } catch {
    /* best-effort */
  }
  clearStoredToken();
  clearCachedUser();

  // Wave 14bz-fix: Service-Worker/HTTP-Cache aggressive bust.
  //
  // Symptom: User klickt Logout, Server revoked die Session + löscht
  // Cookie, localStorage ist leer — ABER nach Reload war der User noch
  // "eingeloggt". Ursache: Workbox-SW oder HTTP-Cache hatte eine
  // erfolgreiche /api/me-Response aus der Vergangenheit gespeichert und
  // lieferte sie (200 + User-Data) statt das frische 401 vom Server
  // zu holen.
  //
  // Fix: alle gecachten Auth-relevanten API-Responses explizit aus dem
  // Cache-Storage löschen. Betrifft /api/me + /api/auth/*. Andere
  // Caches (PMTiles, Trails, POIs) bleiben unberührt — die sind
  // Content und keine Session-State.
  try {
    if ('caches' in globalThis) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(async (name) => {
          const cache = await caches.open(name);
          const reqs = await cache.keys();
          await Promise.all(
            reqs
              .filter((r) => {
                try {
                  const u = new URL(r.url);
                  return (
                    u.pathname === '/api/me' ||
                    u.pathname.startsWith('/api/auth/')
                  );
                } catch {
                  return false;
                }
              })
              .map((r) => cache.delete(r)),
          );
        }),
      );
    }
  } catch (err) {
    console.warn('[auth] logout: cache bust failed', err);
  }
}
